import { resolveActiveVideoClip } from "@/lib/timeline";

// Caption motion (shake / float), object tracking paths and video camera moves.
export const CAMERA_MOVES = [["none", "None"], ["zoom-in", "Slow zoom in"], ["zoom-out", "Slow zoom out"], ["push-in", "Dramatic push-in"],
  ["pan-left", "Pan left"], ["pan-right", "Pan right"], ["handheld", "Handheld drift"], ["shake", "Camera shake"]];
export const CAPTION_MOTIONS = [["none", "None"], ["float", "Float"], ["shake", "Shake"]];

const ease = (u) => u * u * (3 - 2 * u);
const n1 = (t, s) => Math.sin(t * 1.7 + s) * 0.6 + Math.sin(t * 3.1 + s * 2.3) * 0.4;

// Camera transform for a video clip at timeline time t: { scale, dx, dy (fraction of frame), rot (deg) }.
export function cameraTf(clip, t) {
  const c = clip && clip.camera;
  if (!c || !c.type || c.type === "none") return null;
  const a = Math.max(0, Math.min(1, c.intensity ?? 0.6));
  const len = Math.max(0.01, (clip.end || 0) - (clip.start || 0));
  const u = Math.max(0, Math.min(1, (t - (clip.start || 0)) / len));
  switch (c.type) {
    case "zoom-in": return { scale: 1 + 0.25 * a * u, dx: 0, dy: 0, rot: 0 };
    case "zoom-out": return { scale: 1 + 0.25 * a * (1 - u), dx: 0, dy: 0, rot: 0 };
    case "push-in": return { scale: 1 + 0.4 * a * ease(u), dx: 0, dy: -0.02 * a * ease(u), rot: 0 };
    case "pan-left": return { scale: 1 + 0.18 * a, dx: 0.07 * a * (1 - 2 * u), dy: 0, rot: 0 };
    case "pan-right": return { scale: 1 + 0.18 * a, dx: -0.07 * a * (1 - 2 * u), dy: 0, rot: 0 };
    case "handheld": return { scale: 1.06, dx: 0.01 * a * n1(t, 1), dy: 0.008 * a * n1(t * 0.9, 4), rot: 0.5 * a * n1(t * 0.7, 7) };
    case "shake": return { scale: 1.08, dx: 0.022 * a * n1(t * 7, 2), dy: 0.02 * a * n1(t * 8, 5), rot: 1.4 * a * n1(t * 6, 9) };
    default: return null;
  }
}
// fit = { vw, vh, mode, crop } -> clip to the fitted video rect so a letterboxed camera move stays inside its frame.
export function applyCamera(ctx, cam, cw, ch, fit = null) {
  if (!cam) return;
  if (fit && fit.mode === "contain" && fit.vw && fit.vh) {
    const c = fit.crop, vw = fit.vw * (c ? 1 - c.l - c.r : 1), vh = fit.vh * (c ? 1 - c.t - c.b : 1);
    const sc = Math.min(cw / vw, ch / vh), w = vw * sc, h = vh * sc;
    ctx.beginPath(); ctx.rect((cw - w) / 2, (ch - h) / 2, w, h); ctx.clip();
  }
  ctx.translate(cw / 2 + cam.dx * cw, ch / 2 + cam.dy * ch);
  if (cam.rot) ctx.rotate((cam.rot * Math.PI) / 180);
  ctx.scale(cam.scale, cam.scale);
  ctx.translate(-cw / 2, -ch / 2);
}

// Registry so every caption renderer (preview + exports) sees the active camera move.
let tracks = [];
export const setMotionTracks = (mt) => { tracks = mt || []; };
export const camAt = (t) => { const a = resolveActiveVideoClip(tracks, t); return a ? cameraTf(a.o, t) : null; };

// Caption point under a camera move (captions marked "move with camera").
export function camPoint(cam, x, y, W, H) {
  const r = ((cam.rot || 0) * Math.PI) / 180, c = Math.cos(r), s = Math.sin(r);
  const px = (x - W / 2) * cam.scale, py = (y - H / 2) * cam.scale;
  return [W / 2 + cam.dx * W + px * c - py * s, H / 2 + cam.dy * H + px * s + py * c];
}

// Float / shake offsets for a caption: { dx, dy (px), rot (deg) }.
export function motionOffset(m, t, W) {
  if (!m || !m.type || m.type === "none") return null;
  const a = Math.max(0, Math.min(1, m.intensity ?? 0.6)), u = W / 1080;
  if (m.type === "float") return { dx: Math.sin(t * 0.9) * 10 * a * u, dy: Math.sin(t * 1.4) * 22 * a * u, rot: Math.sin(t * 0.7) * 2 * a };
  if (m.type === "shake") return { dx: n1(t * 14, 1) * 9 * a * u, dy: n1(t * 16, 3) * 9 * a * u, rot: n1(t * 12, 5) * 3 * a };
  return null;
}

// Tracked position (canvas-normalised) at time t from track.pts [[t, x, y], ...] plus the caption's offset.
export function trackPos(track, t) {
  const p = track && track.pts;
  if (!p || !p.length) return null;
  let x, y;
  if (t <= p[0][0]) [, x, y] = p[0];
  else if (t >= p[p.length - 1][0]) [, x, y] = p[p.length - 1];
  else {
    let i = 1;
    while (i < p.length && p[i][0] < t) i += 1;
    const [t0, x0, y0] = p[i - 1], [t1, x1, y1] = p[i];
    const k = (t - t0) / Math.max(1e-4, t1 - t0);
    x = x0 + (x1 - x0) * k; y = y0 + (y1 - y0) * k;
  }
  return [x + (track.ox || 0), y + (track.oy || 0)];
}
